/**
 * SkuSettingsTab — הגדרות מק״ט
 *
 * Two generation methods:
 *   1. 'sequential' — auto-increment integer counter (simple, default)
 *   2. 'smart'      — segment-based formula with dictionary mappings
 *
 * Settings are persisted in inventory_company_sku_settings (one row per company).
 * The segment / value wizard is shown ONLY when method = 'smart'.
 *
 * Layout (RTL):
 * ┌─────────────────────────────────────────────────────────┐
 * │  Method selector (radio / toggle)                        │
 * ├─────────────────────────────────────────────────────────┤
 * │  [sequential] Starting number input                      │
 * │  [smart]      Live Preview Bar                           │
 * │  [smart]      Segments Builder  │  Values Dictionary     │
 * └─────────────────────────────────────────────────────────┘
 *
 * Tables used:
 *   inventory_company_sku_settings — method + counter per company
 *   inventory_sku_segments         — formula definition
 *   inventory_sku_values           — per-segment label/code mapping
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Barcode,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  ChevronLeft,
  Hash,
  Tag,
  ArrowUp,
  ArrowDown,
  TriangleAlert,
  BookOpen,
  Minus,
  ListOrdered,
  Sparkles,
  Save,
  Layers,
  Info,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

type SkuMethod = 'sequential' | 'smart' | 'prefixed';

interface CompanySkuSettings {
  company_id:           string;
  sku_generation_method: SkuMethod;
  next_sequential_sku:  number;
}

interface SkuSegment {
  id:             string;
  company_id:     string;
  name:           string;
  char_length:    number;
  position_index: number;
}

interface SkuValue {
  id:         string;
  segment_id: string;
  label:      string;
  code_value: string;
}

interface Props {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── Live Preview Bar ─────────────────────────────────────────────────────────

function PreviewBar({ segments }: { segments: SkuSegment[] }) {
  const sorted = [...segments].sort((a, b) => a.position_index - b.position_index);

  return (
    <div className="bg-gradient-to-l from-orange-50 to-amber-50 border border-orange-200
                    rounded-2xl px-5 py-4 space-y-2.5">
      <div className="flex items-center gap-2">
        <Barcode className="w-4 h-4 text-orange-500 shrink-0" />
        <span className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
          תצוגה מקדימה של פורמט המק״ט
        </span>
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-orange-400 italic">
          הוסף מקטעים כדי לראות את מבנה המק״ט
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5" dir="ltr">
          {sorted.map((seg, i) => (
            <span key={seg.id} className="flex items-center gap-1">
              <span
                className="inline-flex flex-col items-center px-3 py-1.5
                           bg-white border-2 border-orange-300 rounded-lg
                           shadow-sm min-w-[3rem]"
              >
                <span className="text-xs font-bold text-orange-700 tracking-widest font-mono">
                  {'•'.repeat(seg.char_length)}
                </span>
                <span className="text-[10px] text-gray-500 font-medium mt-0.5 max-w-[6rem] truncate">
                  {seg.name}
                </span>
                <span className="text-[9px] text-orange-400 font-mono">
                  {seg.char_length}c
                </span>
              </span>
              {i < sorted.length - 1 && (
                <Minus className="w-3 h-3 text-orange-300 shrink-0" />
              )}
            </span>
          ))}
          {/* Running serial — always appended */}
          <Minus className="w-3 h-3 text-orange-300 shrink-0" />
          <span className="inline-flex flex-col items-center px-3 py-1.5
                           bg-orange-100 border-2 border-orange-400 rounded-lg
                           shadow-sm min-w-[3rem]">
            <span className="text-xs font-bold text-orange-700 tracking-widest font-mono">
              ####
            </span>
            <span className="text-[10px] text-gray-500 font-medium mt-0.5">
              מספר סידורי
            </span>
            <span className="text-[9px] text-orange-400 font-mono">auto</span>
          </span>
        </div>
      )}

      {sorted.length > 0 && (
        <p className="text-[11px] text-orange-500">
          לדוגמה:{' '}
          <span className="font-mono font-semibold" dir="ltr">
            {sorted.map(s => '?'.repeat(s.char_length)).join('-')}-0001
          </span>
        </p>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SkuSettingsTab({ companyId, showToast }: Props) {

  // ── Company SKU settings ──────────────────────────────────────────────────
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [method,          setMethod]          = useState<SkuMethod>('sequential');
  const [nextSeq,         setNextSeq]         = useState<number>(1000);
  const [settingsSaving,  setSettingsSaving]  = useState(false);
  /** Whether the settings row already exists (upsert vs insert) */
  const [settingsExist,   setSettingsExist]   = useState(false);

  // ── Segments state ────────────────────────────────────────────────────────
  const [segments,     setSegments]     = useState<SkuSegment[]>([]);
  const [segLoading,   setSegLoading]   = useState(false);
  const [segError,     setSegError]     = useState<string | null>(null);

  // Segment add/edit modal
  const [segModal,     setSegModal]     = useState(false);
  const [segEditing,   setSegEditing]   = useState<SkuSegment | null>(null);
  const [segName,      setSegName]      = useState('');
  const [segCharLen,   setSegCharLen]   = useState<number>(2);
  const [segPos,       setSegPos]       = useState<number>(0);
  const [segSaving,    setSegSaving]    = useState(false);
  const [segFormErr,   setSegFormErr]   = useState<string | null>(null);

  // Segment delete
  const [segDelTarget, setSegDelTarget] = useState<SkuSegment | null>(null);
  const [segDeleting,  setSegDeleting]  = useState(false);

  // Selected segment (for values panel)
  const [selectedSeg,  setSelectedSeg]  = useState<SkuSegment | null>(null);

  // ── Values state ──────────────────────────────────────────────────────────
  const [values,       setValues]       = useState<SkuValue[]>([]);
  const [valLoading,   setValLoading]   = useState(false);
  const [valError,     setValError]     = useState<string | null>(null);

  // Value add/edit modal
  const [valModal,     setValModal]     = useState(false);
  const [valEditing,   setValEditing]   = useState<SkuValue | null>(null);
  const [valLabel,     setValLabel]     = useState('');
  const [valCode,      setValCode]      = useState('');
  const [valSaving,    setValSaving]    = useState(false);
  const [valFormErr,   setValFormErr]   = useState<string | null>(null);

  // Value delete
  const [valDelTarget, setValDelTarget] = useState<SkuValue | null>(null);
  const [valDeleting,  setValDeleting]  = useState(false);

  // Ref to keep selectedSeg fresh inside async callbacks
  const selectedSegRef = useRef<SkuSegment | null>(null);
  selectedSegRef.current = selectedSeg;

  // ── Fetch company SKU settings ────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    const { data } = await supabase
      .from('inventory_company_sku_settings')
      .select('company_id, sku_generation_method, next_sequential_sku')
      .eq('company_id', companyId)
      .maybeSingle();
    if (data) {
      setMethod((data as CompanySkuSettings).sku_generation_method ?? 'sequential');
      setNextSeq((data as CompanySkuSettings).next_sequential_sku ?? 1000);
      setSettingsExist(true);
    } else {
      setMethod('sequential');
      setNextSeq(1000);
      setSettingsExist(false);
    }
    setSettingsLoading(false);
  }, [companyId]);

  // ── Save company SKU settings ─────────────────────────────────────────────
  const saveSettings = async () => {
    if (nextSeq < 1) { showToast('מספר התחלתי חייב להיות לפחות 1', false); return; }
    setSettingsSaving(true);
    const payload: Partial<CompanySkuSettings> = {
      sku_generation_method: method,
      next_sequential_sku:   nextSeq,
    };
    let err;
    if (settingsExist) {
      ({ error: err } = await supabase
        .from('inventory_company_sku_settings')
        .update(payload)
        .eq('company_id', companyId));
    } else {
      ({ error: err } = await supabase
        .from('inventory_company_sku_settings')
        .insert({ company_id: companyId, ...payload }));
      if (!err) setSettingsExist(true);
    }
    if (err) { showToast(err.message, false); }
    else     { showToast('הגדרות מק״ט נשמרו בהצלחה'); }
    setSettingsSaving(false);
  };

  // ── Fetch segments ────────────────────────────────────────────────────────
  const fetchSegments = useCallback(async () => {
    setSegLoading(true); setSegError(null);
    const { data, error } = await supabase
      .from('inventory_sku_segments')
      .select('id, company_id, name, char_length, position_index')
      .eq('company_id', companyId)
      .order('position_index');
    if (error) { setSegError(error.message); setSegLoading(false); return; }
    const rows: SkuSegment[] = data ?? [];
    setSegments(rows);
    setSegLoading(false);
    if (selectedSegRef.current) {
      const refreshed = rows.find(s => s.id === selectedSegRef.current!.id);
      setSelectedSeg(refreshed ?? null);
    }
  }, [companyId]);

  // ── Fetch values for selected segment ────────────────────────────────────
  const fetchValues = useCallback(async (segId: string) => {
    setValLoading(true); setValError(null);
    const { data, error } = await supabase
      .from('inventory_sku_values')
      .select('id, segment_id, label, code_value')
      .eq('segment_id', segId)
      .order('code_value');
    if (error) { setValError(error.message); setValLoading(false); return; }
    setValues(data ?? []);
    setValLoading(false);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Load segments once method is known (only needed for 'smart', but load anyway so toggle is instant)
  useEffect(() => { if (!settingsLoading) fetchSegments(); }, [settingsLoading, fetchSegments]);

  useEffect(() => {
    if (selectedSeg) fetchValues(selectedSeg.id);
    else             setValues([]);
  }, [selectedSeg, fetchValues]);

  // ── Sorted segments ───────────────────────────────────────────────────────
  const sorted = [...segments].sort((a, b) => a.position_index - b.position_index);

  // Next available position
  const nextPosition = sorted.length > 0
    ? (sorted[sorted.length - 1]?.position_index ?? -1) + 1
    : 0;

  // ── Open segment modal ────────────────────────────────────────────────────
  const openSegAdd = () => {
    setSegEditing(null);
    setSegName(''); setSegCharLen(2); setSegPos(nextPosition);
    setSegFormErr(null); setSegModal(true);
  };
  const openSegEdit = (s: SkuSegment) => {
    setSegEditing(s);
    setSegName(s.name); setSegCharLen(s.char_length); setSegPos(s.position_index);
    setSegFormErr(null); setSegModal(true);
  };
  const closeSegModal = () => {
    setSegModal(false); setSegEditing(null);
    setSegName(''); setSegCharLen(2); setSegPos(0); setSegFormErr(null);
  };

  // ── Validate segment ──────────────────────────────────────────────────────
  const validateSeg = (): string | null => {
    if (!segName.trim())    return 'שם המקטע הוא שדה חובה';
    if (segCharLen < 1)     return 'אורך התווים חייב להיות לפחות 1';
    if (segCharLen > 20)    return 'אורך התווים לא יכול לעלות על 20';
    if (segPos < 0)         return 'מיקום חייב להיות 0 ומעלה';
    const conflict = segments.find(
      s => s.position_index === segPos && s.id !== segEditing?.id
    );
    if (conflict) return `מיקום ${segPos} כבר תפוס על ידי "${conflict.name}"`;
    return null;
  };

  // ── Save segment ──────────────────────────────────────────────────────────
  const handleSegSave = async () => {
    const err = validateSeg();
    if (err) { setSegFormErr(err); return; }
    setSegSaving(true); setSegFormErr(null);
    const payload = {
      name:           segName.trim(),
      char_length:    segCharLen,
      position_index: segPos,
    };
    try {
      if (segEditing) {
        const { error } = await supabase
          .from('inventory_sku_segments')
          .update(payload)
          .eq('id', segEditing.id);
        if (error) throw error;
        showToast('מקטע עודכן בהצלחה');
      } else {
        const { error } = await supabase
          .from('inventory_sku_segments')
          .insert({ company_id: companyId, ...payload });
        if (error) throw error;
        showToast('מקטע נוסף בהצלחה');
      }
      closeSegModal();
      await fetchSegments();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('sku_segments_company_position_unique'))
        setSegFormErr(`מיקום ${segPos} כבר תפוס`);
      else if (msg.includes('sku_segments_company_name_unique'))
        setSegFormErr(`שם "${segName.trim()}" כבר קיים`);
      else
        setSegFormErr(msg);
    } finally {
      setSegSaving(false);
    }
  };

  // ── Delete segment ────────────────────────────────────────────────────────
  const handleSegDelete = async () => {
    if (!segDelTarget) return;
    setSegDeleting(true);
    const { error } = await supabase
      .from('inventory_sku_segments')
      .delete()
      .eq('id', segDelTarget.id);
    if (error) {
      showToast(error.message, false);
    } else {
      showToast('מקטע נמחק (כולל כל הערכים שלו)');
      if (selectedSeg?.id === segDelTarget.id) setSelectedSeg(null);
      setSegDelTarget(null);
      await fetchSegments();
    }
    setSegDeleting(false);
  };

  // ── Quick reorder helpers ─────────────────────────────────────────────────
  const swapPositions = async (segA: SkuSegment, segB: SkuSegment) => {
    const tempPos = Math.max(...segments.map(s => s.position_index)) + 999;
    try {
      await supabase.from('inventory_sku_segments')
        .update({ position_index: tempPos }).eq('id', segA.id);
      await supabase.from('inventory_sku_segments')
        .update({ position_index: segA.position_index }).eq('id', segB.id);
      await supabase.from('inventory_sku_segments')
        .update({ position_index: segB.position_index }).eq('id', segA.id);
    } catch {
      showToast('שגיאה בשינוי סדר המקטעים', false);
    }
    await fetchSegments();
  };

  const moveUp = (seg: SkuSegment) => {
    const idx = sorted.findIndex(s => s.id === seg.id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    if (prev) swapPositions(seg, prev);
  };

  const moveDown = (seg: SkuSegment) => {
    const idx = sorted.findIndex(s => s.id === seg.id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    if (next) swapPositions(seg, next);
  };

  // ── Open value modal ──────────────────────────────────────────────────────
  const openValAdd = () => {
    setValEditing(null); setValLabel(''); setValCode('');
    setValFormErr(null); setValModal(true);
  };
  const openValEdit = (v: SkuValue) => {
    setValEditing(v); setValLabel(v.label); setValCode(v.code_value);
    setValFormErr(null); setValModal(true);
  };
  const closeValModal = () => {
    setValModal(false); setValEditing(null);
    setValLabel(''); setValCode(''); setValFormErr(null);
  };

  // ── Validate value ────────────────────────────────────────────────────────
  const validateVal = (): string | null => {
    if (!valLabel.trim()) return 'תווית היא שדה חובה';
    if (!valCode.trim())  return 'קוד הוא שדה חובה';
    const required = selectedSeg?.char_length ?? 0;
    if (valCode.trim().length !== required)
      return `הקוד חייב להיות בדיוק ${required} תווים (הוזנו: ${valCode.trim().length})`;
    return null;
  };

  // ── Save value ────────────────────────────────────────────────────────────
  const handleValSave = async () => {
    const err = validateVal();
    if (err) { setValFormErr(err); return; }
    if (!selectedSeg) return;
    setValSaving(true); setValFormErr(null);
    const payload = {
      label:      valLabel.trim(),
      code_value: valCode.trim().toUpperCase(),
    };
    try {
      if (valEditing) {
        const { error } = await supabase
          .from('inventory_sku_values')
          .update(payload)
          .eq('id', valEditing.id);
        if (error) throw error;
        showToast('ערך עודכן בהצלחה');
      } else {
        const { error } = await supabase
          .from('inventory_sku_values')
          .insert({ segment_id: selectedSeg.id, ...payload });
        if (error) throw error;
        showToast('ערך נוסף בהצלחה');
      }
      closeValModal();
      fetchValues(selectedSeg.id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('sku_values_segment_code_unique'))
        setValFormErr(`קוד "${valCode.trim()}" כבר קיים במקטע זה`);
      else if (msg.includes('sku_values_segment_label_unique'))
        setValFormErr(`תווית "${valLabel.trim()}" כבר קיימת במקטע זה`);
      else
        setValFormErr(msg);
    } finally {
      setValSaving(false);
    }
  };

  // ── Delete value ──────────────────────────────────────────────────────────
  const handleValDelete = async () => {
    if (!valDelTarget || !selectedSeg) return;
    setValDeleting(true);
    const { error } = await supabase
      .from('inventory_sku_values')
      .delete()
      .eq('id', valDelTarget.id);
    if (error) {
      showToast(error.message, false);
    } else {
      showToast('ערך נמחק');
      setValDelTarget(null);
      fetchValues(selectedSeg.id);
    }
    setValDeleting(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען הגדרות…</span>
      </div>
    );
  }

  return (
    <div className="space-y-5" dir="rtl">

      {/* ── Method Selector Card ─────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Barcode className="w-4 h-4 text-orange-500 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900">שיטת יצירת מק״ט</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Sequential option */}
          <button
            type="button"
            onClick={() => setMethod('sequential')}
            className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-right transition-all
              ${method === 'sequential'
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/40'}`}
          >
            <div className={`mt-0.5 p-1.5 rounded-lg shrink-0
              ${method === 'sequential' ? 'bg-orange-500' : 'bg-gray-100'}`}>
              <ListOrdered className={`w-4 h-4 ${method === 'sequential' ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${method === 'sequential' ? 'text-orange-700' : 'text-gray-800'}`}>
                מספור רץ (מומלץ)
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                כל מוצר חדש מקבל מספר עוקב אוטומטי (1000, 1001, 1002…)
              </p>
            </div>
          </button>

          {/* Smart / formula option */}
          <button
            type="button"
            onClick={() => setMethod('smart')}
            className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-right transition-all
              ${method === 'smart'
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/40'}`}
          >
            <div className={`mt-0.5 p-1.5 rounded-lg shrink-0
              ${method === 'smart' ? 'bg-orange-500' : 'bg-gray-100'}`}>
              <Sparkles className={`w-4 h-4 ${method === 'smart' ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${method === 'smart' ? 'text-orange-700' : 'text-gray-800'}`}>
                פורמולה חכמה
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                הרכב מק״ט ממקטעים עם מילוני קודים (לדוגמה: קטגוריה-מידה-0001)
              </p>
            </div>
          </button>

          {/* Prefixed / supplier option */}
          <button
            type="button"
            onClick={() => setMethod('prefixed')}
            className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border-2 text-right transition-all
              ${method === 'prefixed'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'}`}
          >
            <div className={`mt-0.5 p-1.5 rounded-lg shrink-0
              ${method === 'prefixed' ? 'bg-indigo-500' : 'bg-gray-100'}`}>
              <Layers className={`w-4 h-4 ${method === 'prefixed' ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${method === 'prefixed' ? 'text-indigo-700' : 'text-gray-800'}`}>
                רצף מבוסס ספק
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                הקלד קידומת ספק + כוכבית (1001*) לקבלת המספר הבא ברצף
              </p>
            </div>
          </button>
        </div>

        {/* Prefixed: explanation panel */}
        {method === 'prefixed' && (
          <div className="space-y-3 pt-1">
            <div className="flex items-start gap-3 px-4 py-3.5 bg-indigo-50 border border-indigo-200
                            rounded-xl text-xs text-indigo-800 leading-relaxed">
              <Info className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                <p className="font-semibold text-sm">כיצד פועל מצב ״רצף מבוסס ספק״?</p>
                <ol className="list-decimal list-inside space-y-1 text-indigo-700">
                  <li>
                    בעת הוספת מוצר, הקלד את <strong>קידומת הספק</strong> ואחריה{' '}
                    <span className="font-mono bg-indigo-100 px-1 rounded">*</span>
                    {' '}בשדה המק״ט.{' '}
                    <span className="font-mono bg-white border border-indigo-200 px-1.5 py-0.5 rounded">1001*</span>
                  </li>
                  <li>
                    המערכת תשאל את מסד הנתונים ותמצא את המק״ט הגבוה ביותר שמתחיל ב-<span className="font-mono">1001</span>.
                  </li>
                  <li>
                    אם קיים <span className="font-mono">10018569</span> — השדה יתמלא ב-{' '}
                    <span className="font-mono font-bold">10018570</span>.
                  </li>
                  <li>
                    אם אין מוצרים עם קידומת זו — יתמלא ב-{' '}
                    <span className="font-mono font-bold">10010001</span> (קידומת + 0001).
                  </li>
                </ol>
                <p className="text-indigo-600 pt-1">
                  💡 ניתן לשנות את הערך לאחר המילוי האוטומטי (מצב היברידי).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sequential: starting number input */}
        {method === 'sequential' && (
          <div className="space-y-3 pt-1">
            <div className="flex items-end gap-3">
              <div className="space-y-1.5 flex-1 max-w-[200px]">
                <label className="text-sm font-medium text-gray-700">
                  מספר התחלתי
                </label>
                <input
                  type="number"
                  min={1}
                  value={nextSeq}
                  onChange={e => setNextSeq(parseInt(e.target.value) || 1)}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400">
                  המק״ט הבא שיוקצה לחברה זו
                </p>
              </div>
              <div className="pb-0.5">
                <div className="px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700 font-mono" dir="ltr">
                  {nextSeq} → {nextSeq + 1} → {nextSeq + 2}…
                </div>
              </div>
            </div>

            {/* Hybrid / Override info banner */}
            <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200
                            rounded-xl text-xs text-blue-800 leading-relaxed">
              <Sparkles className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold mb-0.5">מצב היברידי / דריסה (Hybrid Override)</p>
                <p className="text-blue-700">
                  בעת הוספת מוצר חדש, המערכת תציג את המק״ט הבא באופן אוטומטי —
                  אך <strong>תוכל לשנות אותו לכל ערך מותאם אישית</strong>.
                  כאשר תשנה את הערך, המונה הסידורי <em>לא</em> יקודם,
                  כך שיצירת מוצר הבא תציע שוב את אותו מספר עוקב פנוי.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Save button */}
        <div className="flex justify-end pt-1">
          <button
            onClick={saveSettings}
            disabled={settingsSaving}
            className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            {settingsSaving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Save className="w-4 h-4" />
            }
            שמור הגדרות
          </button>
        </div>
      </div>

      {/* ── Smart-mode formula builder (shown only when method = 'smart') ── */}
      {method === 'smart' && (
        <>
          {/* Live Preview Bar */}
          <PreviewBar segments={segments} />

          {/* Two-column master / detail */}
          <div className="flex gap-4 items-start">

            {/* ══ MASTER — Segments Builder ══ */}
            <div className="w-80 shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-orange-500" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">מקטעי המק״ט</h3>
                    <p className="text-[11px] text-gray-400">הגדר את מבנה הפורמולה</p>
                  </div>
                </div>
                <button
                  onClick={openSegAdd}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-600 hover:bg-orange-700
                             text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  הוסף
                </button>
              </div>

              {segError && (
                <div className="mx-4 my-3 flex items-center gap-2 px-3 py-2 bg-red-50
                                border border-red-200 rounded-lg text-xs text-red-700">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />{segError}
                </div>
              )}

              {segLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs">טוען מקטעים…</span>
                </div>
              ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400 px-4 text-center">
                  <Barcode className="w-8 h-8 text-gray-200" />
                  <p className="text-xs font-medium">אין מקטעים עדיין</p>
                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    הוסף מקטע ראשון כדי לבנות את פורמולת המק״ט
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {sorted.map((seg, idx) => {
                    const isSelected = selectedSeg?.id === seg.id;
                    return (
                      <li
                        key={seg.id}
                        onClick={() => setSelectedSeg(isSelected ? null : seg)}
                        className={`flex items-center gap-2 px-4 py-3 cursor-pointer
                                    transition-colors group
                                    ${isSelected
                                      ? 'bg-orange-50 border-r-2 border-orange-500'
                                      : 'hover:bg-gray-50'}`}
                      >
                        {/* Position badge */}
                        <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center
                                         text-[10px] font-bold
                                         ${isSelected
                                           ? 'bg-orange-500 text-white'
                                           : 'bg-gray-100 text-gray-500'}`}>
                          {seg.position_index}
                        </span>

                        {/* Name + char_length */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate
                                         ${isSelected ? 'text-orange-800' : 'text-gray-900'}`}>
                            {seg.name}
                          </p>
                          <p className="text-[11px] text-gray-400 font-mono">
                            {seg.char_length} תווים
                          </p>
                        </div>

                        {/* Detail chevron indicator */}
                        <ChevronLeft
                          className={`w-3.5 h-3.5 shrink-0 transition-transform
                                      ${isSelected ? 'text-orange-500 rotate-180' : 'text-gray-300'}`}
                        />

                        {/* Reorder + actions (shown on hover / selected) */}
                        <div
                          className={`flex flex-col gap-0.5
                                      ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                                      transition-opacity`}
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            onClick={() => moveUp(seg)}
                            disabled={idx === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20
                                       rounded transition-colors"
                            title="הזז למעלה"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => moveDown(seg)}
                            disabled={idx === sorted.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20
                                       rounded transition-colors"
                            title="הזז למטה"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>

                        <div
                          className={`flex gap-0.5
                                      ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                                      transition-opacity`}
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            onClick={() => openSegEdit(seg)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                       rounded transition-colors"
                            title="ערוך"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setSegDelTarget(seg)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50
                                       rounded transition-colors"
                            title="מחק"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {sorted.length > 0 && (
                <div className="px-4 py-2.5 border-t border-gray-50 text-[11px] text-gray-400">
                  {sorted.length} מקטעים · לחץ על מקטע כדי לנהל ערכיו
                </div>
              )}
            </div>

            {/* ══ DETAIL — Values Dictionary ══ */}
            <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

              {!selectedSeg ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3
                                text-gray-400 px-6 text-center">
                  <div className="p-4 bg-gray-50 rounded-2xl">
                    <BookOpen className="w-9 h-9 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">בחר מקטע</p>
                  <p className="text-xs text-gray-400 leading-relaxed max-w-xs">
                    לחץ על מקטע ברשימה כדי לנהל את מילון הערכים שלו —
                    המיפוי בין תווית קריאה לאדם לבין הקוד שיופיע במק״ט
                  </p>
                </div>
              ) : (
                <>
                  {/* Panel header */}
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100
                                  bg-orange-50/40">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 bg-orange-100 rounded-lg">
                        <Tag className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          ערכי "{selectedSeg.name}"
                        </h3>
                        <p className="text-[11px] text-gray-500">
                          כל קוד חייב להיות בדיוק{' '}
                          <span className="font-bold text-orange-600">{selectedSeg.char_length}</span>
                          {' '}תווים
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={openValAdd}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-600 hover:bg-orange-700
                                   text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        הוסף ערך
                      </button>
                      <button
                        onClick={() => setSelectedSeg(null)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                        title="סגור"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {valError && (
                    <div className="mx-5 my-3 flex items-center gap-2 px-3 py-2 bg-red-50
                                    border border-red-200 rounded-lg text-xs text-red-700">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />{valError}
                    </div>
                  )}

                  {valLoading ? (
                    <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-xs">טוען ערכים…</span>
                    </div>
                  ) : values.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 gap-2.5
                                    text-gray-400 px-6 text-center">
                      <Tag className="w-8 h-8 text-gray-200" />
                      <p className="text-xs font-medium">אין ערכים עדיין למקטע זה</p>
                      <p className="text-[11px] text-gray-400 leading-relaxed">
                        הוסף ערכים — כל ערך מגדיר תווית קריאה לאדם + קוד קצר שיופיע במק״ט
                      </p>
                    </div>
                  ) : (
                    <>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                                         text-gray-500 uppercase tracking-wide">
                            <th className="px-5 py-2.5 text-right">תווית</th>
                            <th className="px-5 py-2.5 text-right">קוד במק״ט</th>
                            <th className="px-5 py-2.5 text-right w-24">פעולות</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {values.map(v => (
                            <tr key={v.id} className="hover:bg-gray-50/60 transition-colors group">
                              <td className="px-5 py-3 text-gray-800 font-medium">{v.label}</td>
                              <td className="px-5 py-3">
                                <span className="inline-flex items-center px-2.5 py-1
                                                 bg-orange-50 border border-orange-200
                                                 text-orange-700 text-xs font-mono font-bold
                                                 rounded-lg tracking-widest">
                                  {v.code_value}
                                </span>
                              </td>
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-1
                                                opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => openValEdit(v)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                               rounded-lg transition-colors"
                                    title="ערוך"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => setValDelTarget(v)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                               rounded-lg transition-colors"
                                    title="מחק"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-5 py-2.5 border-t border-gray-50 text-[11px] text-gray-400">
                        {values.length} ערכים · קוד = {selectedSeg.char_length} תווים קבועים
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          MODALS
          ════════════════════════════════════════════════════════════════════ */}

      {/* ── Segment Add / Edit ─────────────────────────────────────────── */}
      {segModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">

            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                {segEditing ? 'עריכת מקטע' : 'הוספת מקטע חדש'}
              </h3>
              <button onClick={closeSegModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {segFormErr && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                              rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />{segFormErr}
              </div>
            )}

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם המקטע <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={segName}
                onChange={e => setSegName(e.target.value)}
                placeholder='לדוגמה: "קטגוריה", "מידה", "עונה"'
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Char length */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  אורך קוד (תווים) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={segCharLen}
                  onChange={e => setSegCharLen(parseInt(e.target.value) || 1)}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400">
                  כל ערך במקטע יחויב להיות בדיוק {segCharLen} תו{segCharLen !== 1 ? 'ות' : ''}
                </p>
              </div>

              {/* Position */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  מיקום <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={segPos}
                  onChange={e => setSegPos(parseInt(e.target.value) || 0)}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <p className="text-[11px] text-gray-400">
                  0 = ראשון משמאל במק״ט
                </p>
              </div>
            </div>

            {/* Example preview inside modal */}
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg
                            text-[11px] text-amber-700 font-mono" dir="ltr">
              {'?'.repeat(segCharLen) || '?'}
              <span className="text-amber-400"> ← placeholder for "{segName || '…'}"</span>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={closeSegModal}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-200
                                 rounded-lg transition-colors hover:text-gray-800">
                ביטול
              </button>
              <button
                onClick={handleSegSave}
                disabled={segSaving || !segName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {segSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {segEditing ? 'שמור שינויים' : 'הוסף מקטע'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Segment Delete Confirm ─────────────────────────────────────── */}
      {segDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <TriangleAlert className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת מקטע</h3>
                <p className="text-sm text-gray-500 mt-1">
                  מחיקת{' '}
                  <span className="font-semibold text-gray-800">"{segDelTarget.name}"</span>
                  {' '}תמחק גם את כל {' '}
                  <span className="font-semibold text-red-600">ערכי המילון</span>
                  {' '}שלו. פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setSegDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-200
                                 rounded-lg transition-colors hover:text-gray-800">
                ביטול
              </button>
              <button onClick={handleSegDelete} disabled={segDeleting}
                      className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                                 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {segDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק מקטע + ערכים
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Value Add / Edit ───────────────────────────────────────────── */}
      {valModal && selectedSeg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">

            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                {valEditing ? 'עריכת ערך' : `הוספת ערך — ${selectedSeg.name}`}
              </h3>
              <button onClick={closeValModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Char-length reminder */}
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200
                            rounded-lg text-xs text-orange-700">
              <Barcode className="w-3.5 h-3.5 shrink-0" />
              הקוד חייב להיות בדיוק{' '}
              <span className="font-bold mx-0.5">{selectedSeg.char_length}</span>
              {' '}תווים (כולל ספרות ואותיות, ללא רווחים)
            </div>

            {valFormErr && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                              rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />{valFormErr}
              </div>
            )}

            {/* Label */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                תווית (שם לתצוגה) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={valLabel}
                onChange={e => setValLabel(e.target.value)}
                placeholder='לדוגמה: "מכנסיים", "בינוני"'
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
            </div>

            {/* Code */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                קוד במק״ט <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={valCode}
                  onChange={e => setValCode(e.target.value.toUpperCase())}
                  placeholder={'?'.repeat(selectedSeg.char_length)}
                  maxLength={selectedSeg.char_length}
                  dir="ltr"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono tracking-widest
                              focus:outline-none focus:ring-2 focus:border-transparent
                              ${valCode.length > 0 && valCode.length !== selectedSeg.char_length
                                ? 'border-red-400 focus:ring-red-400 bg-red-50'
                                : valCode.length === selectedSeg.char_length
                                  ? 'border-green-400 focus:ring-green-400 bg-green-50'
                                  : 'border-gray-300 focus:ring-orange-400'}`}
                />
                {/* Live char counter */}
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs font-mono
                                  pointer-events-none
                                  ${valCode.length === selectedSeg.char_length
                                    ? 'text-green-600' : 'text-gray-400'}`}>
                  {valCode.length}/{selectedSeg.char_length}
                </span>
              </div>
              <p className="text-[11px] text-gray-400">
                יוזן אוטומטית באותיות גדולות. אורך נדרש: {selectedSeg.char_length} תווים.
              </p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={closeValModal}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-200
                                 rounded-lg transition-colors hover:text-gray-800">
                ביטול
              </button>
              <button
                onClick={handleValSave}
                disabled={valSaving || !valLabel.trim() || !valCode.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {valSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {valEditing ? 'שמור שינויים' : 'הוסף ערך'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Value Delete Confirm ───────────────────────────────────────── */}
      {valDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת ערך</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הערך{' '}
                  <span className="font-semibold text-gray-800">"{valDelTarget.label}"</span>
                  {' '}(קוד:{' '}
                  <span className="font-mono font-bold text-orange-700">
                    {valDelTarget.code_value}
                  </span>
                  )? פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setValDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-200
                                 rounded-lg transition-colors hover:text-gray-800">
                ביטול
              </button>
              <button onClick={handleValDelete} disabled={valDeleting}
                      className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                                 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {valDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Exported helpers — used by InventoryProductsPage & VariationWizard ────────

export type { SkuMethod, CompanySkuSettings, SkuSegment, SkuValue };

/** A segment with its full value dictionary pre-loaded */
export interface SkuSegmentWithValues extends SkuSegment {
  values: SkuValue[];
}

/**
 * Fetch the company's SKU settings.
 * Returns null if no row exists yet (treat as sequential/1000 default).
 */
export async function fetchCompanySkuSettings(
  companyId: string
): Promise<CompanySkuSettings | null> {
  const { data } = await supabase
    .from('inventory_company_sku_settings')
    .select('company_id, sku_generation_method, next_sequential_sku')
    .eq('company_id', companyId)
    .maybeSingle();
  return (data as CompanySkuSettings | null) ?? null;
}

/**
 * Fetch all segments for a company, each with their value dictionary.
 * Returns segments sorted by position_index.
 * Used by the inline Smart SKU Builder in the product form.
 */
export async function fetchSmartSkuTemplate(
  companyId: string
): Promise<SkuSegmentWithValues[]> {
  // 1. Load segments
  const { data: segs, error: segErr } = await supabase
    .from('inventory_sku_segments')
    .select('id, company_id, name, char_length, position_index')
    .eq('company_id', companyId)
    .order('position_index');
  if (segErr) throw segErr;
  if (!segs || segs.length === 0) return [];

  // 2. Load all values for those segments in one query
  const segIds = (segs as SkuSegment[]).map(s => s.id);
  const { data: vals, error: valErr } = await supabase
    .from('inventory_sku_values')
    .select('id, segment_id, label, code_value')
    .in('segment_id', segIds)
    .order('label');
  if (valErr) throw valErr;

  // 3. Zip
  const valsBySegment = new Map<string, SkuValue[]>();
  for (const v of (vals ?? []) as SkuValue[]) {
    const arr = valsBySegment.get(v.segment_id) ?? [];
    arr.push(v);
    valsBySegment.set(v.segment_id, arr);
  }

  return (segs as SkuSegment[]).map(seg => ({
    ...seg,
    values: valsBySegment.get(seg.id) ?? [],
  }));
}

/**
 * Allocate `count` sequential SKUs.
 *
 * 1. Read the current next_sequential_sku from DB (fresh read to avoid races).
 * 2. Compute the SKUs: [n, n+1, …, n+count-1].
 * 3. Update the counter to n+count.
 * 4. Return the allocated SKUs as zero-padded strings (min 4 digits).
 */
export async function allocateSequentialSkus(
  companyId: string,
  count: number
): Promise<string[]> {
  // Fresh read
  const { data, error: readErr } = await supabase
    .from('inventory_company_sku_settings')
    .select('next_sequential_sku')
    .eq('company_id', companyId)
    .maybeSingle();

  if (readErr) throw readErr;

  const start = (data as { next_sequential_sku: number } | null)?.next_sequential_sku ?? 1000;
  const skus  = Array.from({ length: count }, (_, i) => String(start + i).padStart(4, '0'));
  const nextVal = start + count;

  // Upsert the incremented counter
  const { error: writeErr } = await supabase
    .from('inventory_company_sku_settings')
    .upsert(
      { company_id: companyId, next_sequential_sku: nextVal, sku_generation_method: 'sequential' },
      { onConflict: 'company_id' }
    );
  if (writeErr) throw writeErr;

  return skus;
}

/**
 * Resolve the next SKU for a given numeric prefix by calling the
 * `get_next_prefixed_sku` Postgres RPC.
 *
 * @param companyId  — company scope
 * @param prefix     — purely-numeric string (e.g. "1001")
 * @returns          full next SKU string (e.g. "10018570" or "10010001")
 * @throws           if the RPC fails or prefix is invalid
 */
export async function resolveNextPrefixedSku(
  companyId: string,
  prefix:    string,
): Promise<string> {
  const { data, error } = await supabase.rpc('get_next_prefixed_sku', {
    p_company_id: companyId,
    p_prefix:     prefix,
  });
  if (error) throw error;
  return data as string;
}
